import { PkBox, PkChip, PkSpinner } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { lazy, memo, Suspense, useEffect, useMemo, useState } from 'react';

import { useStandings } from './hooks';
import StandingTable from './StandingTable';

import { hydrateWithTranslations } from '@services/TranslatorService';
import PromotionPanel from '@shared/PromotionPanel';
import { useTranslateFn } from '@shared/Translate';
import type { League } from '@shared-types/match.types';
import type { GroupStanding, StandingsOptions } from '@shared-types/standing.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { uniqBy } from '@utilities/collection-utils';

import * as classes from './styles.module.scss';

const StandingsCarousel = lazy(() => import('./StandingsCarousel'));

const Wrapper = ({ options, children }: { options: StandingsOptions; children: ReactNode }) => (
  <Suspense fallback={<PkSpinner />}>
    {options.enableCarousel ? <StandingsCarousel options={options}>{children}</StandingsCarousel> : <>{children}</>}
  </Suspense>
);

const SingleStandingTable = memo(
  ({
    className,
    groupStanding,
    ...options
  }: StandingsOptions & { className?: string; groupStanding: GroupStanding }) => {
    if (groupStanding.group)
      return (
        <PkBox className={clsx(classes.box, className)} paddingNone rounded='xl'>
          <StandingTable groupStanding={groupStanding} {...options} />
        </PkBox>
      );

    return <StandingTable groupStanding={groupStanding} {...options} />;
  },
);
SingleStandingTable.displayName = 'SingleStandingTable';

const App = ({ options }: { options: StandingsOptions }) => {
  const { showNotLiveLabel, tracking, showSimulatorPromotion, enableCarousel } = options;
  const t = useTranslateFn();

  const { standings, isLoading, isFetched } = useStandings(options);

  const leagues = useMemo(
    () => uniqBy(standings?.map(({ group }) => group?.league).filter(Boolean), 'id') ?? [],
    [standings],
  );

  const [selectedLeague, setSelectedLeague] = useState<string>(null);
  useEffect(() => {
    if (selectedLeague === null) {
      leagues?.length && setSelectedLeague(leagues.at(0)?.id);
    }
  }, [leagues, selectedLeague]);

  const handleSelectLeague = (league: League) => (e: CustomEvent) => {
    if (tracking) {
      const data = { ...tracking, variant: 'switch league' };
      GoogleTagManagerHelper.gtmCardElementClick(e.target as HTMLElement, data);
    }
    GoogleTagManagerHelper.addVirtualPage({
      virtPath: `/${window.competitionUrl}/standings/${league.id}`,
      league_id: league.id,
      league_name: league.metaData?.leagueName,
    });

    setSelectedLeague(league.id);
  };

  if (isLoading) return <PkSpinner />;

  if (isFetched && !standings.length) return <div>{t('no_data_available')}</div>;

  if (standings.length === 1) {
    return (
      <>
        <SingleStandingTable groupStanding={standings.at(0)} tracking={tracking} {...options} />
        {showNotLiveLabel && (
          <div className='pk-font-size--s pk-text--text-02 pk-line-height--m pk-mt--m'>{t('standings_not_live')}</div>
        )}
        {showSimulatorPromotion && <PromotionPanel />}
      </>
    );
  }

  return (
    <>
      {!!leagues.length && (
        <div className='pk-d--flex pk-gap--xs pk-pb--xs pk-mb--xs pk-gap-sm--m pk-mb-sm--s pk-overflow--auto'>
          {leagues.map((league) => (
            <PkChip
              key={league.id}
              className='pk-flex--shrink-0'
              selected={selectedLeague === league.id}
              onPkClick={handleSelectLeague(league)}
            >
              {hydrateWithTranslations(league).name}
            </PkChip>
          ))}
        </div>
      )}
      <div className={clsx(classes.list, enableCarousel && 'pk-gap--m')}>
        <Wrapper options={options}>
          {standings.map((gs, i) => {
            const hidden = selectedLeague && gs.group?.league?.id !== selectedLeague;
            return (
              <SingleStandingTable
                key={gs.group?.id ?? gs.name ?? `standing-${i}`}
                className={clsx(hidden && 'pk-d--none', enableCarousel && classes.carouselItem)}
                groupStanding={gs}
                tracking={tracking}
                {...options}
              />
            );
          })}
        </Wrapper>
        {!enableCarousel && showNotLiveLabel && (
          <div className='pk-font-size--s pk-text--text-02 pk-line-height--m pk-mt--m'>{t('standings_not_live')}</div>
        )}
      </div>
    </>
  );
};

export default memo(App);
